import React, {createRef, useContext, useState} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import MaterialTable from "@material-table/core";
import {TablePagination, Checkbox, IconButton, useTheme} from "@material-ui/core";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import {ColumnWidth, ValidatorColumn} from "../../../blocks/ColumnHelper";
import {
  MaskedText,
  DatePicker,
  MaskedNumber,
} from "../../../controls";
import AircraftHoursPoolValidator from "../validators/AircraftHoursPoolValidator";
import { useAircraftHoursPools, useAddAircraftHoursPool, useUpdateAircraftHoursPool, useDeleteAircraftHoursPool } from "../../../services/aircraft";
import SystemContext from '../../../SystemContext';
import { viewBlendedRatesPopup } from './ViewBlendedRates';

const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const AircraftHoursPoolList = (_props) => {
  const SystemData = useContext(SystemContext);

  const theme = useTheme();
  const {isAuthenticated} = useAuth0();

  const props = {
    ..._props
  }
  const [state] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddAircraft = useAddAircraftHoursPool();
  const handleUpdateAircraft = useUpdateAircraftHoursPool();
  const handleDeleteAircraft = useDeleteAircraftHoursPool();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const aircraftHoursPools = useAircraftHoursPools({tableRef, queryClient, extraKeys});

  const viewBlendedRates = (rowData) => {
    viewBlendedRatesPopup(`Blended ACMI Rates`, SystemData);
  }


  const getAircraftColumns = (props) => {
    const validatorColumn = ValidatorColumn(AircraftHoursPoolValidator, false);
    const baseColumn = {
      cellStyle: { padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={
      "U": "[A-Z]",
      "A": "[A-Z0-9]",
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9]-",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": ".",
      "C": "[A-Z0-9-]"
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [

      {...validatorColumn},
      {
        ...baseColumn,
        ...(ColumnWidth(300)),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Hours Pool Description",
        field: "description",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(180)),
        title: "Period (Months)",
        field: "poolingPeriodMonths",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(2)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.poolingPeriodMonths}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(280)),
        title: "Monthly Minimum Guaranteed Hours",
        field: "monthlyMinimumGuaranteedHours",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editComponent: (props) => (
          <MaskedNumber
            format={"#".repeat(4)}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.monthlyMinimumGuaranteedHours}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200)),
        title: "ACMI Rate Over MGH",
        field: "acmiRateOverMinimum",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.acmiRateOverMinimum}
            decimalScale={0}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(150)),
        title: "Additional Cost",
        field: "additionalPerHourCost",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <MaskedNumber
            value={props.value}
            decimalScale={2}
            fixedDecimalScale={true}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right" }}
          />
        ),
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.additionalPerHourCost}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(160)),
        cellStyle: { padding: "8px 2px 8px 12px" },
        headerStyle: { padding: "8px 2px 8px 12px" },
        title: "Blended Rate Group",
        field: "blendedRateGroup",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(50)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10)),
        title: "",
        type: "numeric",
        editable: "never",
        initialEditValue: "",
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200)),
        title: "OOS Reduction Allowed",
        field: "outOfServiceReductionAllowed",
        type: "boolean",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.outOfServiceReductionAllowed === true} value="true" />
      },
      {
        ...baseColumn,
        ...(ColumnWidth(140)),
        title: "Valid From",
        field: "validFrom",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
      {
        ...baseColumn,
        title: "Valid To",
        field: "validTo",
        type: "date",
        emptyValue: null,
        initialEditValue: null,
        editable: props.canEdit ? "always" : "never",
        editComponent: (props) => (
          <DatePicker
            // minDate={today}
            value={props.value}
            disableToolbar={true}
            autoOk={true}
            format={dateFormat}
            onChange={props.onChange}
          />
        )
      },
    ];
  };

  return (isAuthenticated &&
      <div style={{width: "100%", marginLeft: 20, marginRight: 20}}>
        <MaterialTable
          tableRef={tableRef}
          style={{
            border: "none",
            boxShadow: "none",
            borderRadius: 0,
          }}
          options={{
            actionsColumnIndex: -1,
            cellStyle: {
              padding: "8px 2px",
            },
            headerStyle: {
              backgroundColor: theme.palette.tableHeader.backgroundColor,
              color: theme.palette.tableHeader.textColor,
              padding: "2px",
            },
            toolbarButtonAlignment: "right",
            pageSize: 10,
            detailPanelType: "single",
            padding: 'dense',
          }}
          actions={[
            {
              icon: 'local_atm',
              tooltip: "Blended Rates",
              isFreeAction: true,
              onClick: (e) => {
                viewBlendedRates();
              }
            },
          ]}
          components={{
            ...defaultComponents,
            Pagination: (props) => (
                <TablePagination
                    {...props}
                    rowsPerPageOptions={[5, 10, 15]}
                />
            ),
          }}
          title="Aircraft Hours Pool Management"
          columns={getAircraftColumns({canEdit: state.canEdit})}
          detailPanel={[]}
          data={aircraftHoursPools.data}
          editable={{
            isEditable: () => {
              return state.canEdit;
            },
            isDeletable: () => {
              return state.canEdit;
            },
            onRowAdd: state.canEdit
                ? (newData) => new Promise((resolve, reject) => {
                  handleAddAircraft.mutate({record: newData, resolve, reject, extraKeys});
                })
                : undefined,
            onRowUpdate: (newData, oldData) =>
                new Promise((resolve, reject) => {
                  handleUpdateAircraft.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
                }),
            onRowDelete: (oldData) =>
                new Promise((resolve, reject) => {
                  handleDeleteAircraft.mutate({oldRecord: oldData, resolve, reject, extraKeys});
                }),
          }}
        />
      </div>
  );

}

export default AircraftHoursPoolList;
